<template>
  <div class="privacy-policy">
    <Navigation :showSearch="false"></Navigation>

    <div class="content">
      <h1>
       Privacy Policy
      </h1>

      <div class="side-nav">
        <h3 class="item-a">
          <a href="#summary">
            Summary
          </a>
        </h3>

        <h3 class="item-a">
          <a href="#categories-personal-collect">
            Categories of Personal Information We Collect
          </a>
        </h3>

        <h3 class="item-a">
          <a href="#sharing-personal">
            Sharing of Personal Information
          </a>
        </h3>

        <h3 class="item-a">
          <a href="#rights-choices">
            Your Rights and Choices
          </a>
        </h3>

        <h3 class="item-a">
          <a href="#cookie-statement">
            Cookie Statement
          </a>
        </h3>
      </div>

      <div id="summary">
        <p>
          At Bongalo, we value you as our customer and we recognize how crucial
          your privacy is. In this Privacy Statement, we explain how we gather,
          use, and disclose your personal information when you use our platform
          and other services we provide, and give you ways to contact us if you
          wish to make an inquiry about your personal data.
        </p>
        <h2>
          Summary
        </h2>
        <p>
          This is a summary of our Privacy Statement. Please scroll down to see
          the full privacy policy statement. .
        </p>
        <h3>1. What this Privacy Statement covers.</h3>
        <p>In this Privacy Statement, we explain the following:</p>
        <ul>
          <li>
            The type of personal information we collect, how we collect it and
            how we use it.
          </li>
          <li>
            When we share your personal information, and whom we share it with.
          </li>
          <li>
            What choices you can make about how we collect, use, and share your
            personal information
          </li>
        </ul>

        <h3>
          2. What personal information we collect and use, and how do we collect
          it.
        </h3>
        <p>We collect your personal information when:</p>
        <ul>
          <li>You give us the information</li>
          <li>We collect it automatically</li>
          <li>We receive it from others</li>
        </ul>
        <p>
          When you create an account on the Bongalo platform, sign up to receive
          offers or information, or make a booking using our platform, you give
          us your personal information. We also collect such facts via automated
          technology inclusive of cookies placed on your browser, with your
          consent where and when applicable, when you visit our site. We also
          receive information from our business partners and other third
          parties, which helps us improve our platform and services, update and
          maintain accurate records, detect and investigate potential fraud, and
          more effectively market our services.
        </p>

        <h3>3. How we share your personal information.</h3>
        <p>
          We may share your personal information to help you make a booking,
          assist you during your travel or stay, communicate with you (including
          sending information about products and services or enabling you to
          communicate with property owners or managers), or to comply with laws.
          Our full Privacy Statement provides additional detail regarding how
          your personal information may be shared.
        </p>
        <h3>4. What are your rights and choices?</h3>
        <p>
          You can exercise your data-safety rights in many ways. For example,
          you can choose to opt out of marketing by clicking the “unsubscribe”
          link in the emails you receive from us, changing preferences in your
          account, as applicable, or by contacting our customer service team.
          You can learn more about your data-safety rights in our full privacy
          statement.
        </p>
        <h3>5. How to contact us.</h3>
        <p>
          You can contact us using the contact details provided in the “Contact”
          section to ask questions about how we process your personal
          information or to make requests about your personal data.
        </p>
      </div>

      <div>
        <h2>
          Full Privacy Statement
        </h2>

        <h3 id="categories-personal-collect">Categories of Personal Information We Collect</h3>
        <p>
          When you use our platform, we may collect the following kinds of
          personal information from you:
        </p>

        <ul>
          <li>
            Name, email address, telephone number; and home, business, and
            billing addresses
          </li>
          <li>
            Government-issued identification as required for booking or identity
            verification, such as passport, driver’s license, and government
            redress numbers; and for vacation-property owners and managers, tax
            identification numbers
          </li>
          <li>
            Payment information such as payment card number, expiration date,
            and billing address
          </li>
          <li>
            Travel-related preferences and requests such as favorite
            destinations and accommodation types, and accessibility needs, as
            available
          </li>
          <li>Birth date and gender</li>
          <li>Geolocation</li>
          <li>Images, videos, and other recordings</li>
          <li>
            Social media account IDs and other publicly available information
          </li>
          <li>
            Communications with us (such as recordings of calls with customer
            service representatives for quality assurance and training purposes)
          </li>
          <li>
            Searches you conduct, transactions into which you enter, and other
            interactions with you on or through our website.
          </li>
          <li>
            Data you provide about other people, such as your travel companions
            or others for whom you are making a booking
          </li>
          <li>
            Information we receive about you from our business partners and
            authorized service providers, which may include updated contact
            information, demographic information, interests, and purchase
            history, which information we may add to your account or profile and
            use for market research and analysis
          </li>
        </ul>
      </div>

      <div>
        <h3>Use of Personal Information</h3>
        <p>
          We use your personal information for various purposes described below.
        </p>
        <h4>Your Use of Online Sites, Apps, and Services:</h4>

        <ul>
          <li>
            Book the requested accommodation or enable accommodation booking
          </li>
          <li>Provide services related to the booking or account</li>
          <li>
            Create, maintain, and update user accounts on our platform and
            authenticate you as a user
          </li>
          <li>
            Maintain your search and travel history, accommodation and travel
            preferences, and similar information about your use of our platform
            and services, and as otherwise described in this Privacy Statement.
          </li>
          <li>
            Enable and facilitate acceptance and processing of payments,
            coupons, and other transactions
          </li>
          <li>Administer loyalty and rewards programs</li>
          <li>Collect and enable booking-related reviews</li>
          <li>
            Help you to use our services faster and more easily through features
            such as the ability to sign-in using your account within our online
            platform.
          </li>
        </ul>
        <h4>Communications and Marketing:</h4>
        <ul>
          <li>
            Respond to your questions and requests for information, and process
            information choices
          </li>
          <li>
            Enable communication between travelers and suppliers, such as
            property owners and managers.
          </li>
          <li>
            Contact you (such as by text message, email, phone call, mail, push
            notification, or messaging on other communication platforms) to
            provide information such as travel booking confirmations and
            updates, for marketing purposes, or for other purposes as described
            in this Privacy Statement
          </li>
          <li>
            Market our products and services, optimize such marketing to be more
            relevant to you, and measure and analyze the effectiveness of our
            marketing and promotions
          </li>
          <li>Administer promotions such as contests, and similar giveaways</li>
        </ul>

        <h4>Other Business Purposes and Compliance</h4>
        <ul>
          <li>Conduct surveys, market research, and data analytics</li>
          <li>
            Maintain, improve, research, and measure the effectiveness of our
            sites and apps, activities, tools, and services
          </li>
          <li>
            Monitor or record calls, chats, and other communications with our
            customer-service team and other representatives, as well as platform
            communications between or among partners and travelers for quality
            control, training, dispute resolution, and as otherwise described in
            this Privacy Statement
          </li>
          <li>
            Create aggregated or otherwise anonymized or de-identified data,
            which we may use and disclose without restriction where permissible
          </li>
          <li>
            Promote security, verify identity of our customers, prevent and
            investigate fraud and unauthorized activities, defend against claims
            and other liabilities, and manage other risks
          </li>
          <li>
            Comply with applicable laws, protect our rights and our users’
            rights and interests, defend ourselves, and respond to law
            enforcement, other legal authorities, and requests that are part of
            legal processes
          </li>
          <li>
            Comply with applicable security and anti-terrorism, anti-bribery,
            customs and immigration, and other such due-diligence laws and
            requirements
          </li>
          <li>
            Operate our business according to what the applicable laws allow.
          </li>
        </ul>
      </div>

      <div>
        <h3 id="sharing-personal">Sharing of Personal Information</h3>
        <p>
          We share your personal information as described below and in this
          Privacy Statement, and as permitted by applicable laws.
        </p>
        <h4>Third-party service providers.</h4>
        <p>
          We share your personal information with third parties in connection
          with the delivery of services to you and the operation of our business
          (for example, to provide payment-card processing, customer service,
          business analytics, and fraud-prevention and compliance services, and
          to serve you with advertising tailored to your interests). These
          third-party service providers are required to protect personal
          information we share with them and may not use any directly
          identifying personal information other than to provide the services
          for which we have contracted them. They are not allowed to use the
          personal information we share for purposes of their own direct
          marketing (unless you have separately consented to such use under the
          terms provided by the third party).
        </p>
        <h4>Travel suppliers.</h4>
        <p>
          We share personal information with travel-related suppliers such as
          property owners and managers, and where available, activity providers,
          and rail and cruise lines who fulfill your booking. Please note that
          travel suppliers may contact you to obtain additional information if
          and as required to facilitate your booking or to otherwise provide the
          travel or associated services.
        </p>

        <h4>Business partners and offers.</h4>
        <p>
          If we promote a program or offer a service or product in conjunction
          with a third-party business partner, we will share your information
          with that partner to assist in marketing or to provide the associated
          product or service. In most of those cases, the program or offer will
          include the name of the third-party business partner, either alone or
          with ours, or you will be redirected to the website of that business
          with notice. An example of such a business-partner relationship would
          be a third-party loyalty program for which you could earn points by
          completing a booking on our platform.
        </p>

        <h4>Other third parties.</h4>
        <p>
          When you access certain features such as Facebook’s “Like” button, or
          use a single sign-on that allows you to login to our online services
          with your social-media credentials, you will share information with
          the social media company or other provider, such as the fact that you
          have visited or interacted with us. For more information, see our
          Cookie Statement. The third-party provider may combine this
          information with other information they have about you. The personal
          information shared will be governed by the third-party provider’s
          privacy policy (including any personal information we may access via
          the third-party provider). These third-party providers should inform
          you about how you can modify your privacy settings on their sites.
        </p>
        <h4>Legal rights and obligations.</h4>
        <p>
          We may disclose your personal information to enforce our policies, or
          where we are permitted (or believe in good faith that we are required)
          to do so by applicable law, such as in response to a request by a law
          enforcement or governmental authority, in connection with actual or
          proposed litigation, or to protect and defend our property, people, or
          other rights or interests. We may also share your personal information
          pursuant to a subpoena or other legal request, or as necessary to
          remit certain taxes in the course of processing payments as required
          by law or legal process.
        </p>
        <h4>Corporate transactions.</h4>
        <p>
          We may share your personal information in connection with a corporate
          transaction, such as a divestiture, merger, consolidation, assignment,
          or asset sale, or in the unlikely event of bankruptcy. In the case of
          an acquisition, we will inform the buyer that it may use your personal
          information only for the purposes disclosed in this Privacy Statement.
        </p>
      </div>

      <div>
        <h3 id="rights-choices">Your Rights and Choices</h3>
        <p>
          You have certain rights and choices with respect to your personal
          information, as described below:
        </p>

        <ul>
          <li>
            If you have an account with us, you may change your communication
            preferences by either (1) logging in and updating the information in
            your account or (2) contacting us through the “Contact ” section
          </li>
          <li>
            You can control our use of certain cookies by following the guidance
            in our Cookie Statement.
          </li>
          <li>
            You can update the accuracy of your information at any time by
            either logging into your account or contacting us
          </li>
          <li>
            If you no longer wish to receive marketing and promotional emails,
            you may unsubscribe by clicking the “unsubscribe” link in the email.
            You can also log into your account to change communication settings.
            Please note that if you choose to unsubscribe from or opt out of
            marketing emails, we may still send you important transactional and
            account-related messages from which you will not be able to
            unsubscribe.
          </li>
          <li>
            If we are processing your personal information on the basis of
            consent, you may withdraw that consent at any time by contacting us.
            Withdrawing your consent will not affect the lawfulness of any
            processing that occurred before you withdrew consent and it will not
            affect our processing of your personal information that is conducted
            in reliance on a legal basis other than consent.
          </li>
        </ul>
      </div>

      <div>
        <h3>International Data Transfer</h3>
        <p>
          The personal information we process may be transmitted or transferred
          to countries other than the country in which you reside. Those
          countries may have data-protection laws that are different from the
          laws of your country.
        </p>
        <p>
          We and our third-party service providers operate in many countries
          around the world. When we collect your personal information, we may
          process it in any of those countries.
        </p>
        <p>
          We require that third-party service providers to whom a data transfer
          is made have appropriate safeguards in place to protect your personal
          information in compliance with applicable data protection law.
        </p>
      </div>

      <div>
        <h3>Security</h3>
        <p>
          We want you to feel confident about using our platform and all
          associated tools and services, and we are committed to taking
          appropriate steps to protect the information we collect. While no
          company can guarantee absolute security, we do take reasonable steps
          to implement appropriate physical, technical, and organizational
          measures to protect the personal information that we collect and
          process.
        </p>
      </div>

      <div>
        <h3>Record Retention</h3>
        <p>
          We will retain your personal information in accordance with all
          applicable laws, for as long as it may be relevant to fulfill the
          purposes set forth in this Privacy Statement, unless a longer
          retention period is required or permitted by law. We will de-identify,
          aggregate, or otherwise anonymize your personal information if we
          intend to use it for analytical purposes or trend analysis over longer
          periods of time.
        </p>
        <p>The criteria we use to determine our retention periods include:</p>

        <ul>
          <li>
            The duration of our relationship with you, including any open
            accounts you may have with us, or recent bookings or other
            transactions you have made on our platform
          </li>
          <li>
            Whether we have a legal obligation related to your personal
            information, such as laws requiring us to keep records of your
            transactions with us
          </li>
          <li>
            Whether there are any relevant legal or tax-related obligations
            affecting how long we will keep your personal information, including
            contract and audit obligations, litigation holds, statutes of
            limitations, and regulatory investigations
          </li>
        </ul>
      </div>

      <div>
        <h3 id="cookie-statement">
          Cookie Statement
        </h3>
        <h4>Types of cookies and similar technologies</h4>

        <h5>Cookies</h5>
        <p>
          Cookies are small pieces of text sent as files to your computer or
          mobile device when you visit most websites. Cookies may be delivered
          by us (first-party cookies) or delivered by a third-party partner or
          supplier (third-party cookies). Cookies are either session cookies or
          persistent cookies. Session cookies enable sites to recognize and link
          the actions of a user during a browsing session and expire at the end
          of each session. Persistent cookies help us recognize you as an
          existing user, and these cookies are stored on your system or device
          until they expire, although you can delete them before the expiration
          date.
        </p>
        <h5>Other similar technologies</h5>
        <ul>
          <li>
            Web beacons, gifs, and clear gifs are tiny graphics, each with a
            unique identifier, that are embedded invisibly on sites and in
            emails. Web beacons allow us to know if a certain page was visited
            or if ad banners on our sites and other sites are effective. We also
            use web beacons in our HTML-based emails to let us know whether our
            emails have been opened by recipients, which helps us to gauge the
            effectiveness of certain communications, promotions, and marketing
            campaigns.
          </li>
          <li>
            Proximity-based beacons send one-way signals over very short
            distances, to communicate with associated apps installed on your
            phone or mobile device. They can notify you, for example, about
            experiences related to your trip and can alert you to related deals
            or promotions. Beacons communicate with your device only when you
            are in close proximity and only if you have given consent within the
            relevant mobile application.
          </li>
          <li>
            Pixels are small objects embedded into a web page that are not
            visible to the user. We use pixels to deliver cookies to your
            computer, facilitate the log-in process, monitor the activity on our
            sites, and deliver online advertising.
          </li>
          <li>
            Tags are small pieces of HTML code that tell your browser to request
            certain content generated by an ad server. We use tags to show you
            relevant advertising and promotions.
          </li>
          <li>
            Scripts are pieces of JavaScript code that launch automatically when
            certain webpages load, to determine whether users view associated
            advertisements.
          </li>
          <li>
            Local Storage Objects, such as HTML 5, are used to store content and
            preferences. Third parties with whom we partner to provide certain
            features on our site to display advertising based upon your web
            browsing activity use Local Storage Objects to collect and store
            information.
          </li>
        </ul>
        <p>
          All of the technologies described above will be collectively referred
          to in this Cookie Statement as “cookies.”
        </p>
        <h4>
          Our use of cookies
        </h4>
        <p>Bongalo use cookies for reasons, including to:</p>

        <ul>
          <li>Improve your experience when visiting our website</li>
          <li>Fulfill transactions and ensure our sites perform as intended</li>
          <li>
            Remember your preferences, such as language, region, or currency
          </li>
          <li>
            Provide you with relevant advertising and analyze performance of the
            ads
          </li>
          <li>Enable you to return to previous travel searches</li>
          <li>Identify errors on our sites</li>
          <li>
            Help with data protection and potentially detect and investigate
            malicious or fraudulent activity
          </li>
          <li>
            Help us understand traffic to our site, including time and date of
            the visit, time and date of the last visit, and other information
          </li>
          <li>Analyze how well our sites are performing</li>
        </ul>

        <h4>
          Types of information collected by cookies
        </h4>
        <p>The types of information that we collect through cookies include:</p>

        <ul>
          <li>IP address</li>
          <li>Device ID</li>
          <li>Viewed pages</li>
          <li>Browser type</li>
          <li>Browsing information</li>
          <li>Operating system</li>
          <li>Internet Service Provider</li>
          <li>
            Whether you have responded to, or interacted with, an advertisement
          </li>
          <li>Referring or referred links or URLs</li>
          <li>
            Features used and activities engaged in on our sites and in our apps
          </li>
          <p>* Pages visited on our site.</p>
          <p>
            See the Categories of Personal Information We Collect section above
            for more information on what types of information are collected
            automatically.
          </p>
        </ul>

        <h4>
          Types and functions of cookies
        </h4>

        <h5>Essential Cookies</h5>
        <p>
          Certain cookies are required or “essential” for our sites to function
          as intended. Essential cookies are necessary for you to navigate our
          site and use certain features like logging in to your account and
          managing your bookings. These cookies are also used to remember
          security settings that allow access to particular content. Lastly, we
          use essential cookies to collect information on which web pages
          visitors go to most, so we can improve our online services. You are
          not able to opt out of essential cookies.
        </p>

        <h5>Other Types of Cookies</h5>
        <p>
          We also use other types of cookies to make our site engaging and
          useful to you:
        </p>

        <ul>
          <li>
            Functional Cookies. We want to make sure that when you visit our
            site, your preferences (such as your preferred language), settings,
            and previous searches are remembered. Functional cookies enhance
            your experience on our site.
          </li>
          <li>
            <p>Analytics Cookies. We use analytics cookies to:</p>
            <ul>
              <li>
                Understand our site’s performance and how it is used, including
                the number of visitors, how long you stay on the site, and which
                parts of the site you visit. We can see details about how
                visitors have interacted with the site, such as the number of
                clicks made on a given page, mouse movements and scrolling
                activity, search words used, and the text entered into various
                fields
              </li>
              <li>Test different designs and features for our site</li>
              <li>Monitor how our visitors reach our sites</li>
              <li>Determine effectiveness of our advertising</li>
              <li>
                Improve our services, including your experience on our site
              </li>
            </ul>
          </li>
        </ul>
        <p>
          For analytics, our third-party service providers may use cookies to
          perform their services and may combine the information that they
          collect about you on our sites with information they have collected
          elsewhere. For example, we use Google Analytics cookies to monitor the
          performance of our sites. Our ability to use and share information
          collected by Google Analytics about your visit to our sites is
          restricted by the Google Analytics Terms of Use and the Google Privacy
          Policy. This Cookie Statement does not cover such third parties’ use
          of your information.
        </p>
        <ul>
          <li>
            Advertising Cookies. We and our third-party advertising partners may
            use advertising cookies to show you advertisements both on and off
            our sites that are based on your interests. We may allow third
            parties to collect information about your online activities through
            cookies to perform their services and may combine the information
            that they collect about you on our sites with other information that
            they have collected. These third parties include business partners
            who collect information when you view or interact with (1) their
            advertisements on our sites; or (2) our advertising or booking
            information on their sites and advertising networks, which collect
            information about your interests when you view or interact with one
            of the advertisements or tracking mechanisms they place on many
            different sites on the Internet. Our partners may make assumptions
            about your interests, characteristics, or preferences and add you to
            groups based on those assumptions in order to show you tailored
            advertisements. Bongalo does not have access to the cookies these
            third parties may use to collect information about your interests,
            and the information practices of these third parties are not covered
            by this Cookie Statement or our Privacy Statement.
          </li>
        </ul>

        <h5>Your information choices</h5>
        <p>
          You can choose not to receive tailored online advertising on this site
          and other sites and learn more about opting out of having your
          information used for tailored advertising purposes. Note that if you
          choose not to receive tailored ads, you will still see online
          advertisements, but they will be general and less relevant to you.
        </p>

        <h5>How can you manage your cookies?</h5>
        <p>
          You can set or amend your web browser controls to accept or refuse
          cookies whenever you like, but please remember if you do choose to
          reject cookies, your access to some of the functionality and areas of
          our site may be restricted.
        </p>

        <h5>Do-Not-Track Signals and Similar Mechanisms.</h5>
        <p>
          Some web browsers may transmit "do not track" signals to sites with
          which the browser communicates. Because of differences in how web
          browsers incorporate and activate this feature, it is not always clear
          whether users intend for these signals to be transmitted, or whether
          users are even aware of them. Participants in the leading
          Internet-standards-setting organization that is addressing this issue
          are in the process of determining what, if anything, sites should do
          when they receive such signals. We currently do not take action in
          response to these signals. If and when a final standard is established
          and accepted, we will reassess our sites’ responses to these signals
          and make appropriate updates to this Cookie Statement.
        </p>

        <h5>Updates to Privacy Statement</h5>
        <p>
          We may update this Privacy Statement in response to changing laws or
          technical or business developments. You can see when this Privacy
          Statement was last updated by checking the “last updated” date
          displayed at the top of this Statement. We shall also send you
          communications informing you of the changes with this privacy
          statement when it happens.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "../components/Blog/Navigation";

export default {
  name: "privacy-policy",
  components: {
    Navigation,
  },
  data: function() {
    return {};
  },
};
</script>

<style lang="scss" scoped>
a {
  text-decoration: none;
}
a:visited {
  color: #3a85fc;
}
.privacy-policy {
  width: 100%;

  .content {
    margin: 0 150px;
    border-left: 1px solid rgb(228, 227, 227);
    border-right: 1px solid rgb(228, 227, 227);
    // border:1px solid red;

    padding: 50px 50px;

    h1 {
      font-style: normal;
      font-weight: bold;
      font-size: 30px;

      color: #404040;
      margin-bottom: 30px;
    }

    .side-nav {
      margin-bottom: 30px;
      width: 100%;
      .item-a {
        font-style: normal;
        font-weight: normal;
        font-size: 16px;
        line-height: 25px;
        /* or 225% */

        text-align: justify;

        color: #3a85fc;
      }
    }
    h2, h3, h4, h5 {
      margin-top: 10px;
    }

    li,
    p {
      width: 90%;
      margin-top: 10px;
      font-style: normal;
      font-weight: normal;
      font-size: 14px;
      line-height: 24px;
      /* or 175% */

      text-align: left;

      color: #404040;
    }
    li {
      margin-top: 10px;
    }
  }
}
</style>
